import React, { useState, useEffect } from 'react';
import { 
  View, 
  Text, 
  StyleSheet, 
  TouchableOpacity, 
  Platform,
  FlatList
} from 'react-native';
import PlaceHolderProducto from './PlaceHolderProducto';
import { BACKEND_URL } from '../../../../Config/Const';

const CarruselOfertas = ({ sucursal = 4 }) => {
  const [productos, setProductos] = useState([]);
  const [productosAleatorios, setProductosAleatorios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Determinar cuántos productos mostrar según el ancho de la pantalla
  const [numProductosAMostrar, setNumProductosAMostrar] = useState(5);
  
  // Escuchar cambios en el tamaño de la ventana (solo en web)
  useEffect(() => {
    const actualizarNumProductos = () => {
      if (Platform.OS === 'web') {
        const windowWidth = window.innerWidth;
        if (windowWidth > 1500) setNumProductosAMostrar(5);
        else if (windowWidth > 1250) setNumProductosAMostrar(4);
        else if (windowWidth > 915) setNumProductosAMostrar(3);
        else if (windowWidth > 630) setNumProductosAMostrar(2);
        else setNumProductosAMostrar(1);
      } else {
        // En dispositivos móviles mostramos 1
        setNumProductosAMostrar(1);
      }
    };

    // Inicializar
    actualizarNumProductos();

    // Solo en web, añadir event listener para resize
    if (Platform.OS === 'web') {
      window.addEventListener('resize', actualizarNumProductos);
      return () => window.removeEventListener('resize', actualizarNumProductos);
    }
  }, []);
  
  // Función para cargar productos desde la API (definida fuera del useEffect para poder llamarla desde otras partes)
  const fetchProductos = async () => {
    try {
      setLoading(true);
      setError(null); // Limpiar errores anteriores
      
      // Construir URL con todos los parámetros requeridos
      const url = new URL(`${BACKEND_URL}/rest/productos/carrusel`);
      
      // Agregar parámetros de consulta
      url.searchParams.append('suc', '4'); // Sucursal fija en 4
      url.searchParams.append('codDep', '0'); // Departamento vacío
      url.searchParams.append('codCat', '0'); // Categoría vacía
      url.searchParams.append('destacados', 'false'); // No destacados
      
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`Error HTTP: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Verificar la estructura de la respuesta y manejar diferentes formatos posibles
      
      if (data && data.dsResultado.productos && Array.isArray(data.dsResultado.productos) && data.dsResultado.productos.length > 0) {
        setProductos(data.dsResultado.productos);
      } else if (data && Array.isArray(data)) {
        setProductos(data);
      } else if (data && typeof data === 'object') {
        const arrayProps = Object.keys(data).filter(key => Array.isArray(data[key]));
        if (arrayProps.length > 0) {
          const firstArrayProp = arrayProps[0];
          setProductos(data[firstArrayProp]);
        } else {
          setProductos([]);
        }
      } else {
        // No se encontró un formato reconocible
        setProductos([]);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error al cargar ofertas del carrusel:', error);
      setError(`No se pudieron cargar las ofertas: ${error.message}`);
      setLoading(false);
    }
  };
  
  // Cargar productos cuando el componente se monta
  useEffect(() => {
    fetchProductos();
    
    return () => {
      // Limpiar cualquier petición pendiente si es necesario
    };
  }, []);
  
  // Seleccionar productos aleatorios cuando la lista de productos cambia
  useEffect(() => {
    if (productos.length > 0 && !loading) {
      // Seleccionar productos aleatorios según el tamaño de pantalla
      const seleccionarProductosAleatorios = () => {
        // Hacer una copia del array original para no modificarlo
        const productosDisponibles = [...productos];
        const seleccionados = [];
        
        // Seleccionar los productos según el ancho de pantalla o menos si no hay suficientes
        // Asegurarse de que la cantidad a seleccionar no sea mayor que los productos disponibles
        const cantidadSeleccionar = Math.min(numProductosAMostrar, productosDisponibles.length);
        
        for (let i = 0; i < cantidadSeleccionar; i++) {
          // Seleccionar un índice aleatorio
          const indiceAleatorio = Math.floor(Math.random() * productosDisponibles.length);
          
          // Agregar el producto al array de seleccionados
          seleccionados.push(productosDisponibles[indiceAleatorio]);
          
          // Eliminar el producto seleccionado para evitar duplicados
          productosDisponibles.splice(indiceAleatorio, 1);
        }
        
        return seleccionados;
      };
      
      // Actualizar el estado con los productos aleatorios
      setProductosAleatorios(seleccionarProductosAleatorios());
    }
  }, [productos, loading, numProductosAMostrar]);
  

  
  // Renderizar un producto individual
  const renderProducto = ({ item }) => {
    return (
      <PlaceHolderProducto
        foto={item.foto || `${BACKEND_URL}/images/productos/${item.plu}.jpg`}
        descripcion={item.descripcion}
        tipo={item.tipo}
        nombre={item.nombre}
        precio={item.precio}
        precioSinImp={item.precioSinImp}
        preUnMed={item.preUnMed}
        plu={item.plu}
        combo={item.combo}
        oferta={item.oferta}
        cuotasSinInt={item.cuotasSinInt}
        minStock={item.minStock}
        umedida={item.umedida}
        cucarda={item.cucarda}
        stock={item.stock}
        maxStock={item.maxStock}
        onAddToCart={(cantidad) => alert(`Agregado ${cantidad} al carrito`)}
      />
    );
  };
  
  // Si está cargando o hay un error, mostrar mensaje apropiado
  if (loading) {
    return (
      <View style={styles.messageContainer}>
        <Text style={styles.messageText}>Cargando ofertas...</Text>
      </View>
    );
  }
  
  // Solo mostrar mensaje de error si hay un error explícito o si terminó de cargar y no hay productos
  if (error || (!loading && productos.length === 0)) {
    return (
      <View style={styles.messageContainer}>
        <Text style={styles.messageText}>
          {error || 'No hay ofertas disponibles en este momento'}
        </Text>
        {__DEV__ && (
          <TouchableOpacity 
            onPress={() => {
              fetchProductos();
            }}
            style={styles.retryButton}
          >
            <Text style={styles.retryButtonText}>Reintentar</Text>
          </TouchableOpacity>
        )}
      </View>
    );
  }
  
  return (
    <View style={styles.container}>
      <View style={styles.headerContainer}>
        <Text style={styles.title}>Productos que te pueden interesar</Text>
      </View>
      
      <View style={styles.productosContainer}>
        {productosAleatorios.map((producto, index) => {
          // Calcular el ancho dinámicamente basado en numProductosAMostrar
          // Ajustar el margen según la cantidad de productos a mostrar
          const margin = numProductosAMostrar >= 4 ? 10 : 15;
          const maxWidth = numProductosAMostrar >= 4 ? 330 : 260;
          
          const itemStyle = {
            ...styles.itemContainer,
            width: Platform.OS === 'web' 
              ? `calc((100% / ${Math.max(1, numProductosAMostrar)}) - ${margin * 2}px)` 
              : '100%',
            maxWidth: Platform.OS === 'web' ? maxWidth : '100%'
          };
          
          return (
            <View key={`producto-${producto.plu}-${index}`} style={itemStyle}>
              {renderProducto({ item: producto })}
            </View>
          );
        })}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
    alignSelf: 'center',
    marginBottom: 20,
    backgroundColor: '#f5f5f5',
  },
  retryButton: {
    marginTop: 15,
    paddingVertical: 8,
    paddingHorizontal: 16,
    backgroundColor: '#e30613',
    borderRadius: 4,
  },
  retryButtonText: {
    color: '#ffffff',
    fontWeight: 'bold',
  },
  headerContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: '3%',
  },
  title: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#e30613',
  },
  productosContainer: {
    flexDirection: 'row',
    justifyContent: 'space-evenly',
    flexWrap: Platform.OS === 'web' ? 'nowrap' : 'wrap',
    paddingHorizontal: 10,
    overflow: 'hidden',
  },
  itemContainer: {
    flexShrink: 0,
    flexGrow: 0,
    marginTop: 10,
    minWidth: Platform.OS === 'web' ? 160 : null,
    // Asegurar que hay suficiente espacio para el contenido
    padding: 5,
  },
  messageContainer: {
    width: '90%',
    maxWidth: 1200,
    alignSelf: 'center',
    padding: 20,
    backgroundColor: '#f5f5f5',
    marginVertical: 20,
    borderRadius: 5,
    alignItems: 'center',
    justifyContent: 'center',
  },
  messageText: {
    fontSize: 16,
    color: '#666',
  }
});

export default CarruselOfertas;
